import { useEffect, useState } from "react";
import Icon from "@/components/icon";
import { cn } from "@/lib/utils";
import { useFeedback } from "@/context/feedback-context";
import type { FeedbackCategory } from "@/types/feedback";

// The header "Opinia" button dispatches this event when a report is on screen:
// the card scrolls itself into view and pulses a highlight ring.
export const FOCUS_REPORT_FEEDBACK_EVENT = "chariot:focus-report-feedback";

// One-tap negative reasons shown after a thumbs-down. Each tap writes its own
// version-tagged doc so triage can count complaints by category.
const REASONS: { key: string; label: string; category: FeedbackCategory }[] = [
  { key: "slow", label: "Za wolno", category: "speed" },
  { key: "missing", label: "Brak źródła", category: "coverage" },
  { key: "wrong", label: "Błędna informacja", category: "accuracy" },
];

type Rating = "up" | "down";

// Report-level feedback: a one-tap thumbs rating first (the lowest-friction
// signal mid-consult), then an OPTIONAL follow-up — reason chips on a
// thumbs-down, free text either way. The rating writes immediately; the
// follow-up enriches it but is never required. Switching the thumb writes a
// new doc — `feedback` docs are append-only (Firestore rules deny updates), so
// the latest doc by created_at is the user's current stance.
export function FeedbackBar() {
  const { submitFeedback, submitting } = useFeedback();
  const [rating, setRating] = useState<Rating | null>(null);
  const [sentReasons, setSentReasons] = useState<Set<string>>(new Set());
  const [comment, setComment] = useState("");
  const [commentSent, setCommentSent] = useState(false);
  const [highlighted, setHighlighted] = useState(false);

  useEffect(() => {
    let timer: number | undefined;
    const onFocus = () => {
      document
        .getElementById("report-feedback")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      // Restart the pulse when the event fires again while still highlighted.
      setHighlighted(false);
      requestAnimationFrame(() => {
        setHighlighted(true);
        window.clearTimeout(timer);
        timer = window.setTimeout(() => setHighlighted(false), 1600);
      });
    };
    window.addEventListener(FOCUS_REPORT_FEEDBACK_EVENT, onFocus);
    return () => {
      window.removeEventListener(FOCUS_REPORT_FEEDBACK_EVENT, onFocus);
      window.clearTimeout(timer);
    };
  }, []);

  const rate = (next: Rating) => {
    if (rating === next) return;
    setRating(next);
    void submitFeedback({
      scope: "report",
      sentiment: next === "up" ? "positive" : "negative",
      category: next === "up" ? "praise" : "other",
    });
  };

  const tapReason = (reason: (typeof REASONS)[number]) => {
    if (sentReasons.has(reason.key)) return;
    setSentReasons((prev) => new Set(prev).add(reason.key));
    void submitFeedback({
      scope: "report",
      sentiment: "negative",
      category: reason.category,
    });
  };

  const sendComment = () => {
    const text = comment.trim();
    if (!text || commentSent || !rating) return;
    setCommentSent(true);
    void submitFeedback({
      scope: "report",
      sentiment: rating === "up" ? "positive" : "negative",
      category: "other",
      text,
    });
  };

  return (
    <section
      id="report-feedback"
      aria-label="Ocena raportu"
      className={cn(
        "rounded-xl border-2 border-brand/25 bg-brand/[0.05] px-5 py-5 transition-shadow sm:px-6",
        highlighted && "shadow-lg ring-2 ring-brand/60 ring-offset-2 ring-offset-background",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <span className="text-base font-semibold text-foreground">
          Czy ten raport był pomocny?
        </span>
        <div className="flex gap-2.5">
          <button
            type="button"
            onClick={() => rate("up")}
            disabled={submitting}
            aria-pressed={rating === "up"}
            className={cn(
              "inline-flex min-h-11 items-center gap-2 rounded-xl border px-5 text-sm font-medium transition-colors",
              rating === "up"
                ? "border-brand bg-brand/[0.12] text-brand"
                : "border-border bg-card text-foreground hover:border-brand/50 hover:bg-brand/[0.08] hover:text-brand disabled:opacity-50",
            )}
          >
            <Icon name="thumbs-up" width={18} height={18} />
            Tak
          </button>
          <button
            type="button"
            onClick={() => rate("down")}
            disabled={submitting}
            aria-pressed={rating === "down"}
            className={cn(
              "inline-flex min-h-11 items-center gap-2 rounded-xl border px-5 text-sm font-medium transition-colors",
              rating === "down"
                ? "border-destructive/60 bg-destructive/[0.10] text-destructive"
                : "border-border bg-card text-foreground hover:border-destructive/50 hover:bg-destructive/[0.06] hover:text-destructive disabled:opacity-50",
            )}
          >
            <Icon name="thumbs-down" width={18} height={18} />
            Nie
          </button>
        </div>
        {rating === "up" && !commentSent && (
          <span className="inline-flex items-center gap-1.5 text-sm text-brand">
            <Icon name="check" width={14} height={14} />
            Dziękujemy!
          </span>
        )}
      </div>

      {rating && (
        <div className="mt-4 border-t border-brand/15 pt-4">
          {rating === "down" && (
            <>
              <p className="text-sm font-medium text-foreground">
                Co poszło nie tak?{" "}
                <span className="font-normal text-muted-foreground">
                  (opcjonalnie)
                </span>
              </p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {REASONS.map((reason) => {
                  const isSent = sentReasons.has(reason.key);
                  return (
                    <button
                      key={reason.key}
                      type="button"
                      onClick={() => tapReason(reason)}
                      disabled={isSent || submitting}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors",
                        isSent
                          ? "border-destructive/40 bg-destructive/[0.08] text-destructive"
                          : "border-border bg-card text-foreground hover:border-destructive/40 hover:bg-destructive/[0.06] hover:text-destructive",
                      )}
                    >
                      {isSent && <Icon name="check" width={13} height={13} />}
                      {reason.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {!commentSent ? (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                aria-label={
                  rating === "up" ? "Co było pomocne?" : "Co poprawić?"
                }
                placeholder={
                  rating === "up"
                    ? "Co było pomocne? (opcjonalnie)"
                    : "Co poprawić? (opcjonalnie)"
                }
                className="w-full resize-none rounded-lg border border-input bg-card px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              <button
                type="button"
                onClick={sendComment}
                disabled={!comment.trim() || submitting}
                className="inline-flex shrink-0 items-center justify-center gap-1.5 self-stretch rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50 max-sm:min-h-11"
              >
                <Icon name="send" width={14} height={14} />
                Wyślij
              </button>
            </div>
          ) : (
            <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-brand">
              <Icon name="check" width={13} height={13} />
              Dziękujemy za opinię.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
